import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
import { ModelInterfacesSchema } from '../assets/access.js';
import { AssetCategorySchema } from '../assets/categories.js';
import { BatteryChemistrySchema, BatteryTechnologySchema } from '../common/battery.js';
// Products catalog (ADR-065) — response shapes. canonical_key is loader
// plumbing and never rendered (deliberately absent from every view shape).
export const AssetModelSchema = z
    .object({
    row_version: RowVersionSchema,
    id: z.string(),
    category: AssetCategorySchema,
    manufacturer: z.string().nullable(),
    name: z.string(),
    series: z.string().nullable(),
    part_number: z.string().nullable(),
    description: z.string().nullable(),
    is_canonical: z.boolean(),
    is_generic: z.boolean(),
    is_network_device: z.boolean().nullable(),
    // The device half. capabilities reads as plain strings — a newer content
    // bundle may carry tokens this release doesn't know, and the catalog view
    // shows them; logic (the access read's service derivation) filters to the
    // known set. interfaces is the FORMAT five-key facet ({} = none evidenced).
    capabilities: z.array(z.string()),
    interfaces: ModelInterfacesSchema,
    power_kw: z.number().nullable(),
    capacity_kva: z.number().nullable(),
    capacity_ah: z.number().nullable(),
    voltage_v: z.number().nullable(),
    watt_peak_w: z.number().nullable(),
    cooling_kw: z.number().nullable(),
    tank_l: z.number().nullable(),
    battery_chemistry: BatteryChemistrySchema.nullable(),
    battery_technology: BatteryTechnologySchema.nullable(),
    warranty_months: z.number().int().nullable(),
    is_disabled: z.boolean(),
})
    .strict();
// The vendor's market state, from the catalog record's lifecycle facet —
// tolerant passthrough (content moves code-free; unknown keys/tokens render
// raw). The DETAIL read alone carries it (list reads never select the record
// content — ADR-065's guards); `successor` resolves to the successor model's
// row when the key matches a loaded canonical.
export const ModelLifecycleSchema = z
    .object({
    status: z.string(),
    successor: z.string().optional(),
})
    .passthrough();
// A record's artifact reference (ems-domain ADR-008 / ems-catalog FORMAT
// §Artifacts): the record CITES a document by content address; the bytes arrive
// separately as artifact packs into the instance's `catalog/blobs/<sha256>`
// prefix — store-as-truth, never a `files` row (ADR-068 §1). `kind` is a
// tolerant string (content moves code-free; the renderer maps the known kinds —
// datasheet · manual · brochure · mib · drawing · certificate — and falls back);
// sha256 · size · mime are format law and validate strictly at the import door.
export const CatalogArtifactSchema = z
    .object({
    kind: z.string().min(1).max(32),
    title: z.string().min(1).max(160),
    doc: z.string().max(120).optional(),
    rev: z.string().max(40).optional(),
    lang: z.string().max(8).optional(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    size: z.number().int().positive(),
    mime: z.string().min(1).max(127),
    /** false = citation-only (login-gated vendor originals — never packed). */
    redistributable: z.boolean().optional(),
})
    .passthrough();
/** The detail read's artifact row: the citation + whether THIS instance's store
 *  holds the bytes (a pack landed) — false renders the honest "not yet imported". */
export const CatalogArtifactItemSchema = CatalogArtifactSchema.extend({ available: z.boolean() });
// The PROJECT-scoped item: the base facts + how many of THIS project's assets
// reference the row (consumers: the store cards' "N units here" line + the
// peek's Usage section; "this project's products" stays derived from use).
export const AssetModelProjectItemSchema = AssetModelSchema.extend({
    in_use_count: z.number().int(),
    // Detail-read extras (undefined on list items):
    lifecycle: ModelLifecycleSchema.nullable().optional(),
    lifecycle_successor: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
    // Documents & evidence citations + curator notes, from the record content
    // (canonical rows only — locals' truth IS their columns). Citations are doc
    // number + revision strings, never URLs (FORMAT §Field law); `artifacts` are
    // the sha-addressed documents themselves (above) — an evidence line whose
    // document is referenced rides there, the rest stay citation-only.
    evidence: z.array(z.string()).optional(),
    artifacts: z.array(CatalogArtifactItemSchema).optional(),
    notes: z.string().nullable().optional(),
}).strict();
// Front-page list (pagination: none — the catalog is instance-bounded curated
// content; the client filters/sorts the full set, the admin-pages pattern).
export const AssetModelListResponseSchema = z
    .object({ items: z.array(AssetModelProjectItemSchema), total: z.number().int() })
    .strict();
// The picker's search item — the row carries its suggestion GROUP as every lookup
// row does (ADR-080 §6): `in_use` = used in this project (derived from use — no
// pivot exists to read), a window of LOOKUP_GROUP_CAP rows at rest, every match
// once typed; null for an ordinary row. The category's GENERIC row is pinned
// LAST in every answer of that category — a real row (its id is what a generic
// unit stores), never in the group, never cut by the cap.
export const AssetModelSearchItemSchema = AssetModelProjectItemSchema.extend({
    group: z.enum(['in_use']).nullable(),
}).strict();
export const AssetModelSearchResponseSchema = z
    .object({ items: z.array(AssetModelSearchItemSchema), has_more: z.boolean() })
    .strict();
export const AssetModelIdResponseSchema = z.object({ id: z.string() }).strict();
// ── The sysadmin catalog surface (version · history · proposals · merge) ────
export const CatalogImportEntrySchema = z
    .object({
    id: z.number().int(),
    kind: z.enum(['content', 'artifacts']),
    content_version: z.number().int().nullable(),
    format_version: z.number().int(),
    source: z.enum(['image', 'upload', 'online']),
    actor: z.object({ id: z.string(), label: z.string() }).nullable(), // null = system (boot)
    counts: z.record(z.string(), z.number().int()),
    created_at: z.string(),
})
    .strict();
export const CatalogStatusResponseSchema = z
    .object({
    content_version: z.number().int().nullable(), // null = never imported (generics only)
    format_version: z.number().int(), // the app's supported format
    canonical_count: z.number().int(), // offered canonicals (generics excluded)
    not_offered_count: z.number().int(), // withdrawn canonicals — the card states the whole truth
    local_count: z.number().int(),
    imports: z.array(CatalogImportEntrySchema), // recent first; bounded history
})
    .strict();
// Merge proposals are READ-DERIVED (never persisted): a local row matching a
// canonical row (CI name or part-number equality within the category) IS the
// proposal — always fresh after every import ("local rows ARE the model
// requests", ADR-065).
export const MergeProposalSchema = z
    .object({
    local: AssetModelSchema,
    canonical: AssetModelSchema,
    match: z.enum(['name', 'part_number']),
    assets_affected: z.number().int(),
})
    .strict();
export const MergeProposalsResponseSchema = z
    .object({ items: z.array(MergeProposalSchema) })
    .strict();
// The sysadmin locals inventory — every project-homed local across the
// instance ("local rows ARE the model requests"): where it lives, how many
// assets stand on it (zeros matter — a zero-asset local is safe to disable),
// and whether it is still offered. Platform surface only.
export const LocalModelEntrySchema = z
    .object({
    id: z.string(),
    category: AssetCategorySchema,
    manufacturer: z.string().nullable(),
    name: z.string(),
    is_disabled: z.boolean(),
    project: z.object({ id: z.string(), name: z.string() }),
    assets_count: z.number().int(),
})
    .strict();
export const LocalModelsResponseSchema = z.object({ items: z.array(LocalModelEntrySchema) }).strict();
export const MergeResultResponseSchema = z
    .object({
    winner_id: z.string(),
    loser_id: z.string(),
    reassigned_assets: z.number().int(),
    affected_projects: z.number().int(),
})
    .strict();
// The import door's answer (content bundles + artifact packs share the door;
// counts keys differ by kind — content: created/updated/unchanged/content_only,
// packs: blobs_added/blobs_present).
export const CatalogImportResultSchema = z
    .object({
    kind: z.enum(['content', 'artifacts']),
    content_version: z.number().int().nullable(),
    counts: z.record(z.string(), z.number().int()),
})
    .strict();
